// ---------------------------------------------------------------------------
// InfoTextPickerDialog — Modal to pick or create informational text
// ---------------------------------------------------------------------------

import { useState, useMemo } from 'react';
import { Search, Plus } from 'lucide-react';
import {
  useInformationalTexts,
  useCreateInformationalText,
  useInfoCategories,
} from '@/hooks/use-informational-texts';
import { useAuth } from '@/lib/auth';
import { canCreateQuestion } from '@/types/workflow';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/toast';
import type { InformationalText, InfoTextFilters } from '@/types/question';

interface InfoTextPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (item: InformationalText) => void;
}

export function InfoTextPickerDialog({
  open,
  onOpenChange,
  onSelect,
}: InfoTextPickerDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [keyword, setKeyword] = useState('');
  const [category, setCategory] = useState('__all__');
  const [showCreate, setShowCreate] = useState(false);
  const { data: categories = [] } = useInfoCategories();

  const filters: InfoTextFilters = useMemo(
    () => ({
      keyword: keyword || undefined,
      category: category === '__all__' ? undefined : category,
      page: 1,
      pageSize: 20,
    }),
    [keyword, category],
  );

  const { data, isLoading } = useInformationalTexts(filters);
  const createMutation = useCreateInformationalText();

  const [newBody, setNewBody] = useState('');
  const [newCategory, setNewCategory] = useState('');

  async function handleCreateAndSelect() {
    try {
      const result = await createMutation.mutateAsync({
        body: newBody,
        category: newCategory || undefined,
      });
      toast({ title: 'Informational text created', variant: 'success' });
      onSelect(result);
      onOpenChange(false);
    } catch {
      // Error handled by mutation
    }
  }

  /** Truncate long text for card preview */
  function preview(text: string, max = 120): string {
    return text.length > max ? text.slice(0, max) + '...' : text;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pick Informational Text</DialogTitle>
          <DialogDescription>
            Select existing text from the bank or create a new entry.
          </DialogDescription>
        </DialogHeader>

        {showCreate ? (
          <div className="space-y-3 mt-2">
            <div>
              <Label className="mb-1">Text</Label>
              <Textarea
                value={newBody}
                onChange={(e) => setNewBody(e.target.value)}
                placeholder="Enter informational text shown to the patient..."
                className="min-h-[80px]"
              />
            </div>
            <div>
              <Label className="mb-1">Category (optional)</Label>
              <Input
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="e.g. Welcome, Closing, Instructions"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>
                Back
              </Button>
              <Button
                onClick={handleCreateAndSelect}
                disabled={!newBody.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create & Select'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-carbon-400" />
                <Input
                  placeholder="Search informational texts..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {user && canCreateQuestion(user.role) && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowCreate(true)}
                >
                  <Plus className="h-4 w-4 mr-1" /> New
                </Button>
              )}
            </div>

            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-16" />
                ))}
              </div>
            ) : data?.entries.length === 0 ? (
              <p className="text-sm text-carbon-500 text-center py-6">
                No informational texts found
              </p>
            ) : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {data?.entries.map((item) => (
                  <Card
                    key={item.informational_text_id}
                    className="cursor-pointer hover:border-purple-300 hover:bg-purple-50/30 transition-colors"
                    onClick={() => {
                      onSelect(item);
                      onOpenChange(false);
                    }}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-carbon-800">
                            {preview(item.body)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {item.category && (
                            <Badge variant="outline" className="text-[10px]">
                              {item.category}
                            </Badge>
                          )}
                          <Badge variant="secondary" className="text-[10px]">
                            I-{item.informational_text_id}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
